import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2, Edit, DollarSign, Search } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  researching: "bg-gray-100 text-gray-600",
  applied: "bg-blue-50 text-blue-700",
  pending: "bg-amber-50 text-amber-700",
  awarded: "bg-emerald-50 text-emerald-700",
  denied: "bg-red-50 text-red-700",
  completed: "bg-warm-50 text-warm-700",
};

export const GrantsPanel = ({ getAuthHeader }) => {
  const [grants, setGrants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "", organization: "", amount: "", deadline: "", description: "",
    requirements: "", contact_name: "", contact_email: "", notes: "",
  });

  const fetchGrants = async () => {
    try {
      const [grantsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/grants`, getAuthHeader()),
        axios.get(`${API}/admin/grants/stats`, getAuthHeader()),
      ]);
      setGrants(grantsRes.data);
      setStats(statsRes.data);
      setLoaded(true);
    } catch { toast.error("Failed to load grants"); }
  };

  if (!loaded) fetchGrants();

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/grants`, { ...form, amount: parseFloat(form.amount) || 0 }, getAuthHeader());
      toast.success("Grant added");
      setShowForm(false);
      setForm({ name: "", organization: "", amount: "", deadline: "", description: "", requirements: "", contact_name: "", contact_email: "", notes: "" });
      fetchGrants();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const update = { status: newStatus };
      if (newStatus === "applied") update.submitted_date = new Date().toISOString().split("T")[0];
      if (newStatus === "awarded") update.awarded_date = new Date().toISOString().split("T")[0];
      await axios.put(`${API}/admin/grants/${id}`, update, getAuthHeader());
      toast.success("Updated");
      fetchGrants();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this grant?")) return;
    try {
      await axios.delete(`${API}/admin/grants/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchGrants();
    } catch { toast.error("Failed"); }
  };

  const filtered = grants.filter(g =>
    `${g.name} ${g.organization}`.toLowerCase().includes(search.toLowerCase())
  );

  const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-xs text-emerald-600 font-medium">Awarded</p>
            <p className="text-xl font-bold text-emerald-700">{fmt(stats.total_awarded_amount)}</p>
            <p className="text-xs text-emerald-500">{stats.awarded} grants</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4">
            <p className="text-xs text-amber-600 font-medium">Pending/Applied</p>
            <p className="text-xl font-bold text-amber-700">{fmt(stats.total_pending_amount)}</p>
            <p className="text-xs text-amber-500">{stats.pending + stats.applied} grants</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-xs text-blue-600 font-medium">Total Tracked</p>
            <p className="text-xl font-bold text-blue-700">{stats.total_grants}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4">
            <p className="text-xs text-red-600 font-medium">Denied</p>
            <p className="text-xl font-bold text-red-700">{stats.denied}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search grants..." className="pl-10" />
        </div>
        <Button data-testid="add-grant-btn" onClick={() => setShowForm(!showForm)} className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
          <Plus className="w-4 h-4" />{showForm ? "Cancel" : "Add Grant"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Grant Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Organization *</Label><Input value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Amount ($)</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Deadline</Label><Input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Contact Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Requirements</Label><textarea value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" /></div>
          <div><Button type="submit" className="bg-warm-700 hover:bg-warm-800 text-white">Add Grant</Button></div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Grant</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium hidden md:table-cell">Organization</th>
            <th className="text-right py-3 px-4 text-gray-500 font-medium">Amount</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium hidden sm:table-cell">Deadline</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
            <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(g => (
              <tr key={g.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-3 px-4 font-medium text-gray-900">{g.name}</td>
                <td className="py-3 px-4 text-gray-600 hidden md:table-cell">{g.organization}</td>
                <td className="py-3 px-4 text-right font-medium">{fmt(g.amount)}</td>
                <td className="py-3 px-4 text-gray-600 hidden sm:table-cell">{g.deadline || "-"}</td>
                <td className="py-3 px-4">
                  <select value={g.status} onChange={e => handleStatusChange(g.id, e.target.value)} className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${statusColors[g.status]}`}>
                    <option value="researching">Researching</option><option value="applied">Applied</option><option value="pending">Pending</option><option value="awarded">Awarded</option><option value="denied">Denied</option><option value="completed">Completed</option>
                  </select>
                </td>
                <td className="py-3 px-4 text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(g.id)} className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-gray-400">No grants found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
